import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Deck } from "@/types/linguadeck";
import { useAuth } from "@/contexts/AuthContext";
import { useGamification } from "@/contexts/GamificationContext";
import { useGamificationStore } from "@/stores/useGamificationStore";
import { useTimeTrackingStore, type ModeSeconds } from "@/stores/useTimeTrackingStore";
import { useMasteryMedals } from "@/hooks/useAchievements";
import {
  fetchDashboardCollectionCounts,
  fetchSrsStats,
  fetchUserActivity,
  fetchUserActivityByMode,
  fetchDifficultCards,
  type SrsStatsData,
  type UserActivityDay,
  type DifficultCard,
  type TimeDistributionModeKey,
} from "@/lib/db";
import { computeWeeklyForecast } from "@/utils/weeklyForecast";
import TimeActivityChart from "@/components/Statistics/TimeActivityChart";
import { BookOpen, Activity, Medal, Trophy, Star, Crown, Gem, Flame, Layers, Globe, Diamond, GraduationCap, Sparkles } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  TooltipProps,
} from "recharts";

interface DashboardViewProps {
  decks: Deck[];
  points: number;
  onSelectDeck: (id: string) => void;
  /** Opens quick-study modal for a specific card (from "Repeat" in difficult words) */
  onRepeatCard?: (deckId: string, cardId: string) => void;
}

/** Theme-aware chart colors (3 colors for area/bar charts) */
function getChartColors(equippedTheme: string | null): [string, string, string] {
  switch (equippedTheme) {
    case "cyberpunkTheme":
      return ["#fce205", "#00f0ff", "#ff003c"];
    case "sakuraTheme":
      return ["hsl(350, 70%, 55%)", "hsl(340, 80%, 60%)", "hsl(330, 75%, 65%)"];
    case "matrixTheme":
      return ["#00FF41", "#00dd37", "#00bb2d"];
    case "neonTheme":
      return ["#ff00ff", "#00ffff", "#ffff00"];
    default:
      return ["hsl(var(--primary))", "hsl(var(--accent2))", "hsl(var(--accent3))"];
  }
}

/** Four distinct colors for Time Distribution modes (cards, learn, test, games) — works in light/dark */
function getTimeDistributionColors(equippedTheme: string | null): [string, string, string, string] {
  switch (equippedTheme) {
    case "cyberpunkTheme":
      return ["#fce205", "#00f0ff", "#ff003c", "#ff8c00"]; // Orange for games
    case "sakuraTheme":
      return ["hsl(350, 70%, 55%)", "hsl(340, 80%, 60%)", "hsl(200, 70%, 55%)", "#f28b46"]; // Orange-ish for games
    case "matrixTheme":
      return ["#00FF41", "#00dd37", "#00ffff", "#ff8c00"]; // Orange for games
    case "neonTheme":
      return ["#ff00ff", "#00ffff", "#ffff00", "#ff6600"]; // Orange for games
    default:
      return [
        "hsl(var(--primary))",
        "hsl(var(--accent2))",
        "hsl(var(--accent3))",
        "#f97316", // Tailwind orange-500 equivalent
      ];
  }
}

/** Theme-aware tooltip styles — premium dashboard feel */
function getTooltipStyles(equippedTheme: string | null): React.CSSProperties {
  const base = {
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 13,
    boxShadow: "0 8px 24px rgba(0,0,0,0.14), 0 2px 6px rgba(0,0,0,0.08)",
  };
  switch (equippedTheme) {
    case "cyberpunkTheme":
      return {
        ...base,
        backgroundColor: "#0d0221",
        border: "1px solid rgba(252,226,5,0.4)",
        color: "#ffffff",
      };
    case "sakuraTheme":
      return {
        ...base,
        backgroundColor: "rgba(255,255,255,0.98)",
        border: "1px solid rgba(233,105,110,0.3)",
        color: "#1e293b",
      };
    case "matrixTheme":
      return {
        ...base,
        backgroundColor: "#0d0d0d",
        border: "1px solid rgba(0,255,65,0.4)",
        color: "#00FF41",
      };
    case "neonTheme":
      return {
        ...base,
        backgroundColor: "hsl(280, 80%, 6%)",
        border: "1px solid rgba(255,0,255,0.4)",
        color: "#ff00ff",
      };
    default:
      return {
        ...base,
        backgroundColor: "hsl(var(--card-bg))",
        border: "1px solid hsl(var(--border))",
        color: "hsl(var(--foreground))",
      };
  }
}

/** Premium tooltip content — clear hierarchy, refined typography */
function ChartTooltipContent(
  props: TooltipProps<number, string> & { contentStyle: React.CSSProperties; formatter?: (v: number, n: string) => [number | string, string]; labelFormatter?: (label: unknown, payload: unknown[]) => string }
) {
  const { active, payload, label, contentStyle, formatter, labelFormatter } = props;
  if (!active || !payload?.length) return null;
  const displayLabel = labelFormatter ? labelFormatter(label, payload) : String(label ?? "");
  const items = payload.map((p) => {
    const v = p.value as number;
    const n = String(p.name ?? "");
    const [dispVal, dispName] = formatter ? formatter(v, n) : [v, n];
    return { name: dispName, value: dispVal };
  });
  return (
    <div style={contentStyle}>
      {displayLabel && (
        <div style={{ fontWeight: 500, marginBottom: 6, fontSize: 11, opacity: 0.85, letterSpacing: "0.03em", textTransform: "uppercase" }}>
          {displayLabel}
        </div>
      )}
      {items.map(({ name, value }) => (
        <div key={name} style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "baseline" }}>
          <span style={{ fontSize: 12, opacity: 0.9 }}>{name}</span>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{value}</span>
        </div>
      ))}
    </div>
  );
}

export default function DashboardView({ decks, points, onSelectDeck, onRepeatCard }: DashboardViewProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { crystals, currentStreak: streak, equippedTheme, studyHistory } = useGamification();
  const syncTimeTotals = useGamificationStore((s) => s.syncTimeTotals);
  const siteSeconds = useTimeTrackingStore((s) => s.siteSeconds);
  const studySeconds = useTimeTrackingStore((s) => s.studySeconds);
  const modeSeconds = useTimeTrackingStore((s) => s.modeSeconds);
  const persistedSiteMinutes = Math.max(Math.floor(siteSeconds / 60), siteSeconds > 0 ? 1 : 0);
  const persistedStudyMinutes = Math.max(Math.floor(studySeconds / 60), studySeconds > 0 ? 1 : 0);
  const [srsStats, setSrsStats] = useState<SrsStatsData | null>(null);
  const [activityByDate, setActivityByDate] = useState<Record<string, UserActivityDay>>({});
  const [activityByMode, setActivityByMode] = useState<{ cards: number; learn: number; test: number; games: number } | null>(null);
  const [difficultCards, setDifficultCards] = useState<DifficultCard[]>([]);
  const [collectionCounts, setCollectionCounts] = useState<{ deckCount: number; cardCount: number } | null>(null);

  const chartColors = useMemo(() => getChartColors(equippedTheme), [equippedTheme]);
  const tooltipStyle = useMemo(() => getTooltipStyles(equippedTheme), [equippedTheme]);

  useEffect(() => {
    if (!user?.id) return;
    fetchSrsStats(user.id)
      .then(setSrsStats)
      .catch(() => setSrsStats(null));
  }, [user?.id, decks]);

  useEffect(() => {
    if (!user?.id) {
      setCollectionCounts(null);
      return;
    }
    fetchDashboardCollectionCounts(user.id)
      .then(setCollectionCounts)
      .catch((err) => {
        console.warn("[DashboardView] fetchDashboardCollectionCounts failed:", err);
        setCollectionCounts(null);
      });
  }, [user?.id, decks]);

  const fetchActivity = React.useCallback(() => {
    if (!user?.id) return;
    fetchUserActivity(user.id, "all")
      .then(setActivityByDate)
      .catch(() => setActivityByDate({}));
  }, [user?.id]);

  const fetchActivityByModeData = React.useCallback(() => {
    if (!user?.id) return;
    fetchUserActivityByMode(user.id, 30)
      .then(setActivityByMode)
      .catch(() => setActivityByMode(null));
  }, [user?.id]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  useEffect(() => {
    fetchActivityByModeData();
  }, [fetchActivityByModeData]);

  // Refetch all stats when dashboard becomes visible (e.g. after study session or tab switch)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible" || !user?.id) return;
      fetchActivity();
      fetchActivityByModeData();
      fetchSrsStats(user.id).then(setSrsStats).catch(() => setSrsStats(null));
      fetchDifficultCards(user.id, 10).then(setDifficultCards).catch(() => setDifficultCards([]));
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [fetchActivity, fetchActivityByModeData, user?.id]);

  useEffect(() => {
    if (Object.keys(activityByDate).length === 0) return;
    let totalStudy = 0;
    let totalSite = 0;
    for (const day of Object.values(activityByDate)) {
      totalStudy += day.minutes ?? 0;
      totalSite += day.total_site_minutes ?? 0;
    }
    syncTimeTotals(totalStudy, totalSite);
  }, [activityByDate, syncTimeTotals]);

  useEffect(() => {
    if (!user?.id) return;
    fetchDifficultCards(user.id, 10)
      .then(setDifficultCards)
      .catch(() => setDifficultCards([]));
  }, [user?.id, decks]);

  const loadedCardsCount = decks.reduce((s, d) => s + (d.cards?.length ?? 0), 0);
  const totalDecks = collectionCounts
    ? Math.max(collectionCounts.deckCount, decks.length)
    : decks.length;
  const totalCards = collectionCounts
    ? Math.max(collectionCounts.cardCount, loadedCardsCount)
    : loadedCardsCount;
  const countsReady = collectionCounts !== null;

  const langCounts = { en: 0, es: 0, fr: 0 };
  decks.forEach((d) => {
    if (d.lang in langCounts) langCounts[d.lang as keyof typeof langCounts]++;
  });

  /** Last 30 days: index 0 = Today (left), index 29 = 30 days ago (right) */
  const activityDots = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const historySet = new Set(studyHistory);
    const out: { date: string; count: number }[] = [];
    for (let i = 0; i < 30; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i); // i=0 → Today, i=29 → 30 days ago
      const key = d.toISOString().slice(0, 10);
      const day = activityByDate[key];
      let count = day?.count ?? 0;
      if (count === 0) {
        if (historySet.has(key)) count = 1;
        else if ((day?.minutes ?? 0) > 0 || (day?.total_site_minutes ?? 0) > 0) count = 1;
        else if (key === todayStr && (persistedStudyMinutes > 0 || persistedSiteMinutes > 0)) count = 1;
      }
      out.push({ date: key, count });
    }
    return out;
  }, [activityByDate, studyHistory, persistedStudyMinutes, persistedSiteMinutes]);

  /** Short date format for chart X-axis: "22.02" — avoids label overlap */
  const formatDateShort = (dateStr: string) => {
    const d = new Date(dateStr + "T12:00:00");
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  /** Full date for heatmap tooltip: "22.02.2026" — lets user identify Today vs 30 days ago */
  const formatDateForTooltip = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-");
    return `${d}.${m}.${y}`;
  };

  /** Format day label for weekly bar chart (Today, Tomorrow, or "DD.MM Day") */
  const formatWeeklyDayLabel = (dateStr: string) => {
    const todayStr = new Date().toISOString().slice(0, 10);
    if (dateStr === todayStr) return t("statistics.today");
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (dateStr === tomorrow.toISOString().slice(0, 10)) return t("statistics.tomorrow");
    const d = new Date(dateStr + "T12:00:00");
    const dayKeys = ["daySun", "dayMon", "dayTue", "dayWed", "dayThu", "dayFri", "daySat"] as const;
    const dayShort = t(`statistics.${dayKeys[d.getDay()]}`);
    return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, "0")} ${dayShort}`;
  };

  /** Library Mastery: prefer srsStats.masteryPie (DB: all user cards) — decks often lack cards (fetchDecksLight) */
  const libraryMasteryData = useMemo(() => {
    if (srsStats?.masteryPie?.length) {
      return srsStats.masteryPie;
    }
    const allCards = decks.flatMap((d) => d.cards ?? []);
    let newCount = 0;
    let inProgressCount = 0;
    let masteredCount = 0;
    for (const c of allCards) {
      const rep = c.repetition_count ?? 0;
      const interval = c.interval_days ?? 0;
      if (rep === 0) newCount++;
      else if (interval >= 21) masteredCount++;
      else inProgressCount++;
    }
    return [
      { name: "new", value: newCount },
      { name: "learning", value: inProgressCount },
      { name: "mastered", value: masteredCount },
    ].filter((x) => x.value > 0);
  }, [srsStats?.masteryPie, decks]);

  /** Weekly Load: from SRS stats (Supabase) when available, else from local decks */
  const upcomingByDayWithLabels = useMemo(() => {
    const raw =
      srsStats?.upcomingByDay?.length
        ? srsStats.upcomingByDay
        : computeWeeklyForecast(decks.flatMap((d) => d.cards ?? []));
    return raw.map((row) => ({ ...row, label: formatWeeklyDayLabel(row.date) }));
  }, [srsStats?.upcomingByDay, decks, i18n.language]);

  /** Lifetime: from first activity date through today. Always include today if we have persisted time. */
  const allActivityDates = useMemo(() => {
    const keys = Object.keys(activityByDate).sort();
    const today = new Date().toISOString().slice(0, 10);
    const hasPersistedToday = persistedSiteMinutes > 0 || persistedStudyMinutes > 0;
    if (keys.length === 0) {
      return [today];
    }
    const first = keys[0];
    const last = keys[keys.length - 1];
    const endDate = last > today ? last : today;
    const out: string[] = [];
    const cur = new Date(first + "T12:00:00");
    const end = new Date(endDate + "T12:00:00");
    while (cur <= end) {
      out.push(cur.toISOString().slice(0, 10));
      cur.setDate(cur.getDate() + 1);
    }
    if (hasPersistedToday && out[out.length - 1] !== today) {
      out.push(today);
    }
    return out;
  }, [activityByDate, persistedSiteMinutes, persistedStudyMinutes]);

  const todayStr = new Date().toISOString().slice(0, 10);

  /** Fixed 30-day window for Time Analytics charts — continuous timeline with explicit 0 for inactive days (creates peaks and valleys). */
  const TIME_ANALYTICS_DAYS = 30;
  const timeAnalyticsDates = useMemo(() => {
    const out: string[] = [];
    const today = new Date();
    for (let i = TIME_ANALYTICS_DAYS - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }, [todayStr]);

  /** Aggregate by week when > 90 days to avoid chart clutter */
  const AGGREGATION_THRESHOLD = 90;
  const aggregateByWeek = allActivityDates.length > AGGREGATION_THRESHOLD;

  /** Build daily sessions for study time. Uses fixed 30-day window with explicit 0 for inactive days. */
  const rawStudySessions = useMemo(() => {
    return timeAnalyticsDates.map((date) => {
      const dbVal = activityByDate[date]?.minutes ?? 0;
      const persisted = date === todayStr ? persistedStudyMinutes : 0;
      return { date, time: Math.max(dbVal, persisted) };
    });
  }, [activityByDate, timeAnalyticsDates, todayStr, persistedStudyMinutes]);

  /** Build daily sessions for site time. Uses fixed 30-day window with explicit 0 for inactive days. */
  const rawSiteSessions = useMemo(() => {
    return timeAnalyticsDates.map((date) => {
      const dbVal = activityByDate[date]?.total_site_minutes ?? 0;
      const persisted = date === todayStr ? persistedSiteMinutes : 0;
      return { date, time: Math.max(dbVal, persisted) };
    });
  }, [activityByDate, timeAnalyticsDates, todayStr, persistedSiteMinutes]);

  /** Aggregate daily sessions by week (Monday-based) */
  const aggregateSessionsByWeek = (sessions: { date: string; time: number }[]) => {
    const byWeek = new Map<string, { dates: string[]; totalTime: number }>();
    for (const row of sessions) {
      const d = new Date(row.date + "T12:00:00");
      const dayOfWeek = d.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      d.setDate(d.getDate() + mondayOffset);
      const weekKey = d.toISOString().slice(0, 10);
      const existing = byWeek.get(weekKey);
      if (!existing) byWeek.set(weekKey, { dates: [row.date], totalTime: row.time });
      else {
        existing.dates.push(row.date);
        existing.totalTime += row.time;
      }
    }
    return Array.from(byWeek.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([_, v]) => ({ date: v.dates[v.dates.length - 1], time: v.totalTime }));
  };

  /** Ensure at least 2 points so AreaChart draws a line (not just a dot) */
  const ensureMinTwoPoints = (sessions: { date: string; time: number }[]) => {
    if (sessions.length >= 2) return sessions;
    if (sessions.length === 0) {
      const today = new Date().toISOString().slice(0, 10);
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return [
        { date: yesterday.toISOString().slice(0, 10), time: 0 },
        { date: today, time: 0 },
      ];
    }
    const first = sessions[0];
    const prev = new Date(first.date + "T12:00:00");
    prev.setDate(prev.getDate() - 1);
    const prevDate = prev.toISOString().slice(0, 10);
    return [{ date: prevDate, time: 0 }, ...sessions];
  };

  /** Trim leading zero-value days so chart doesn't start with a long flat line.
   *  Keeps up to 2 zero-days before the first non-zero for a smooth ramp. */
  const trimLeadingZeros = (sessions: { date: string; time: number }[]) => {
    const firstNonZero = sessions.findIndex((s) => s.time > 0);
    if (firstNonZero <= 2) return sessions; // already short or no zeros
    const keepFrom = Math.max(0, firstNonZero - 2);
    return sessions.slice(keepFrom);
  };

  /** Learning time per day — real study minutes per day (not cumulative) */
  const learningTimeChartData = useMemo(() => {
    const trimmed = trimLeadingZeros(rawStudySessions);
    const withMin = ensureMinTwoPoints(trimmed);
    return withMin.map((s) => ({
      date: s.date,
      label: formatDateShort(s.date),
      time: s.time,
    }));
  }, [rawStudySessions]);

  /** App time per day — real site minutes per day (not cumulative) */
  const siteTimeChartData = useMemo(() => {
    const trimmed = trimLeadingZeros(rawSiteSessions);
    const withMin = ensureMinTwoPoints(trimmed);
    return withMin.map((s) => ({
      date: s.date,
      label: formatDateShort(s.date),
      time: s.time,
    }));
  }, [rawSiteSessions]);

  /** Time Distribution: time per mode (cards, learn, test, games) from user_card_events. Segments with value > 0 only. */
  const timeDistributionColors = useMemo(() => getTimeDistributionColors(equippedTheme), [equippedTheme]);
  const MODE_ORDER: TimeDistributionModeKey[] = ["cards", "learn", "test", "games"];
  const MODE_I18N: Record<TimeDistributionModeKey, string> = {
    cards: "statistics.modeCards",
    learn: "statistics.learnWords",
    test: "statistics.tests",
    games: "statistics.games",
  };

  const timeDistributionData = useMemo(() => {
    const db = activityByMode ?? { cards: 0, learn: 0, test: 0, games: 0 };
    // Merge live session seconds (converted to minutes) so current session shows instantly
    const liveMinutes: ModeSeconds = {
      cards: Math.round((modeSeconds.cards / 60) * 10) / 10,
      learn: Math.round((modeSeconds.learn / 60) * 10) / 10,
      test: Math.round((modeSeconds.test / 60) * 10) / 10,
      games: Math.round((modeSeconds.games / 60) * 10) / 10,
    };
    const by = {
      cards: Math.max(db.cards, liveMinutes.cards),
      learn: Math.max(db.learn, liveMinutes.learn),
      test: Math.max(db.test, liveMinutes.test),
      games: Math.max(db.games, liveMinutes.games),
    };
    const total = by.cards + by.learn + by.test + by.games;
    if (total <= 0) return [];

    const segments: { key: TimeDistributionModeKey; name: string; value: number; percent: number; color: string }[] = [];
    MODE_ORDER.forEach((key, i) => {
      const value = by[key];
      if (value > 0) {
        segments.push({
          key,
          name: MODE_I18N[key],
          value,
          percent: Math.round((value / total) * 100),
          color: timeDistributionColors[i],
        });
      }
    });
    return segments;
  }, [activityByMode, modeSeconds, timeDistributionColors]);

  /** Weekly study time bar chart — last 7 days from real activity data */
  const weeklyStudyBarData = useMemo(() => {
    const dayKeys = ["daySun", "dayMon", "dayTue", "dayWed", "dayThu", "dayFri", "daySat"] as const;
    const result: { day: string; minutes: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const dbVal = activityByDate[key]?.minutes ?? 0;
      const persisted = key === todayStr ? persistedStudyMinutes : 0;
      result.push({
        day: t(`statistics.${dayKeys[d.getDay()]}`),
        minutes: Math.max(dbVal, persisted),
      });
    }
    return result;
  }, [activityByDate, todayStr, persistedStudyMinutes, i18n.language]);

  const { medals: masteryMedals } = useMasteryMedals();

  const statusLabel = (s: DifficultCard["status"]) => t(`statistics.status.${s}`);

  const formatTime = (totalMinutes: number) => {
    const h = Math.floor(totalMinutes / 60);
    const m = Math.round(totalMinutes % 60);
    return `${h}:${String(m).padStart(2, "0")} ${t("statistics.minutesFull")}`;
  };

  /** Compact format for legend: "2ч 14м" or "55м" */
  const formatTimeCompact = (totalMinutes: number) => {
    const h = Math.floor(totalMinutes / 60);
    const m = Math.round(totalMinutes % 60);
    if (h > 0) return `${h}${t("statistics.hoursShort")} ${m}${t("statistics.minutesShort")}`;
    return `${m}${t("statistics.minutesShort")}`;
  };


  const statItems = [
    { val: crystals.toLocaleString(), label: t("statistics.crystals"), icon: Gem, color: "hsl(var(--accent2))", to: "/gamification?tab=shop" },
    { val: streak.toLocaleString(), label: t("statistics.streakDays"), icon: Flame, color: "hsl(var(--accent3))", to: "/gamification?tab=streaks" },
    { val: countsReady ? totalDecks.toLocaleString() : "—", label: t("statistics.decks"), icon: BookOpen, color: "hsl(var(--foreground))", to: "/library" },
    { val: countsReady ? totalCards.toLocaleString() : "—", label: t("statistics.cards"), icon: Layers, color: "hsl(var(--foreground))", to: "/library" },
  ];

  return (
    <div className="statistics-page overflow-hidden w-full max-w-full px-4 sm:px-6">
      {/* Page header */}
      <header className="statistics-header">
        <h1 className="statistics-header-title">{t("statistics.pageTitle")}</h1>
        <p className="statistics-header-subtitle">{t("statistics.pageSubtitle")}</p>
      </header>

      {/* Section: Overview — key metrics */}
      <section className="statistics-section">
        <h2 className="statistics-section-title">{t("statistics.sectionOverview")}</h2>
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4 min-w-0">
          {statItems.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.label}
                to={s.to}
                className="statistics-card cursor-pointer block no-underline text-inherit"
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                <div className="statistics-metric-value" style={{ color: s.color }}>{s.val}</div>
                <div className="statistics-metric-label flex items-center gap-2">
                  <Icon size={16} className="opacity-70" />
                  <span>{s.label}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Section: Progress & mastery */}
      {(() => {
        const MEDAL_ICONS: Record<string, React.ComponentType<{ size?: number; color?: string; strokeWidth?: number }>> = {
          medalFirstStep: Medal,
          medalDiligentStudent: GraduationCap,
          medalLanguageAthlete: Trophy,
          medalTimeMaster: Crown,
          medalMasterStudyDo: Star,
          medalLegend: Crown,
          medalPolyglot: Diamond,
          medalAbsolute: Sparkles,
        };
        return (
          <section className="statistics-section">
            <h2 className="statistics-section-title">{t("statistics.sectionProgress")}</h2>
            <div className="statistics-card min-w-0 overflow-hidden">
              <div className="statistics-card-title" style={{ fontSize: 16, marginBottom: "1.25rem" }}>
                {t("statistics.masteryMedals")}
              </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {masteryMedals.map(({ medal, isUnlocked, progressLabel, progressPercent }) => {
                const MedalIcon = MEDAL_ICONS[medal.nameKey] ?? Medal;
                return (
                  <div
                    key={medal.id}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                      padding: 16,
                      borderRadius: "var(--radius)",
                      background: isUnlocked ? "hsl(var(--bg3))" : "transparent",
                    }}
                  >
                    <div
                      style={{
                        filter: isUnlocked
                          ? `drop-shadow(0 0 10px ${medal.glowColor}) drop-shadow(0 0 20px ${medal.glowColor})`
                          : "grayscale(100%) opacity(0.5)",
                        transition: "filter 0.3s ease",
                      }}
                    >
                      <MedalIcon size={40} color={isUnlocked ? medal.color : "#6b7280"} strokeWidth={1.5} />
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        textAlign: "center",
                        color: isUnlocked ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
                      }}
                    >
                      {t(`statistics.${medal.nameKey}`)}
                    </div>
                    <div style={{ fontSize: 10, color: "hsl(var(--muted-foreground))", textAlign: "center" }}>
                      {isUnlocked ? (
                        <>✓ {medal.hours.toLocaleString()}{t("statistics.hoursShort")}</>
                      ) : (
                        <>
                          {progressLabel}{t("statistics.hoursShort")}
                          <div
                            style={{
                              marginTop: 4,
                              height: 3,
                              background: "hsl(var(--bg3))",
                              borderRadius: 2,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                width: `${Math.min(100, progressPercent)}%`,
                                background: medal.color,
                                borderRadius: 2,
                                transition: "width 0.3s ease",
                              }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          </section>
        );
      })()}

      {/* Section: Activity & time */}
      <section className="statistics-section">
        <h2 className="statistics-section-title">{t("statistics.sectionActivity")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 min-w-0">
        {/* By language */}
        <div className="statistics-card min-w-0 overflow-hidden">
          <div className="statistics-card-title">{t("statistics.decksByLanguage")}</div>
          {(["en", "es", "fr"] as const).map((lang) => {
            const count = langCounts[lang];
            const pct = decks.length ? (count / decks.length) * 100 : 0;
            return (
              <div key={lang} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                  <span className="flex items-center gap-1.5"><Globe size={14} className="opacity-60" />{lang.toUpperCase()}</span>
                  <span>{count}</span>
                </div>
                <div style={{ height: 8, background: "hsl(var(--bg3))", borderRadius: 999, overflow: "hidden" }}>
                  <div
                    className="progress-fill transition-[width] duration-500 ease-out"
                    style={{
                      height: "100%",
                      background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent2)))",
                      borderRadius: 999,
                      width: `${pct}%`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Activity heatmap */}
        <div className="statistics-card min-w-0 overflow-hidden">
          <div className="statistics-card-title">{t("statistics.activity30Days")}</div>
          <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }} title={t("statistics.cardsPerDay")}>
            {activityDots.map((dot) => {
              const maxCount = Math.max(1, ...activityDots.map((d) => d.count));
              const intensity = maxCount > 0 ? dot.count / maxCount : 0;
              const bg =
                dot.count === 0
                  ? "hsl(var(--bg3))"
                  : intensity >= 0.7
                  ? chartColors[0]
                  : intensity >= 0.3
                  ? chartColors[1]
                  : "rgba(0,229,160,.4)";
              const tooltipDate = formatDateForTooltip(dot.date);
              const tooltipText = dot.count > 0
                ? `${tooltipDate} — ${dot.count} ${t("statistics.cards")}`
                : tooltipDate;
              return (
                <div
                  key={dot.date}
                  className="activity-dot"
                  style={{ background: bg }}
                  title={tooltipText}
                />
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 10, opacity: 0.9 }}>
            {activityDots.every((d) => d.count === 0)
              ? t("statistics.noActivityYet")
              : t("statistics.last30Days")}
          </div>
        </div>
        </div>

        {/* SRS Reports */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 min-w-0 mt-5">
        {/* Report A: Upcoming Workload — Bar Chart */}
        <div className="statistics-card min-w-0 overflow-hidden">
          <div className="statistics-card-title">{t("statistics.weeklyLoad")}</div>
          {upcomingByDayWithLabels.some((d) => d.count > 0) || (totalCards > 0 && totalDecks > 0) ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={upcomingByDayWithLabels} margin={{ top: 12, right: 12, left: 8, bottom: 8 }}>
              <defs>
                <linearGradient id="weeklyLoadBarGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColors[0]} stopOpacity={1} />
                  <stop offset="100%" stopColor={chartColors[0]} stopOpacity={0.75} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 8" stroke="hsl(var(--border))" strokeOpacity={0.35} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))", opacity: 0.9 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))", opacity: 0.9 }} axisLine={false} tickLine={false} />
              <Tooltip
                wrapperStyle={{ outline: "none" }}
                content={(props) => (
                  <ChartTooltipContent
                    {...props}
                    contentStyle={tooltipStyle}
                    formatter={(v: number) => [v, t("statistics.cards")]}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.label}
                  />
                )}
              />
              <Bar dataKey="count" fill="url(#weeklyLoadBarGrad)" radius={[6, 6, 0, 0]} isAnimationActive animationDuration={400} />
            </BarChart>
          </ResponsiveContainer>
          ) : (
            <div className="statistics-empty" style={{ minHeight: 220 }}>
              <Activity size={36} className="statistics-empty-icon" />
              <span>{t("statistics.noData")}</span>
            </div>
          )}
        </div>

        {/* Report B: Library Mastery — Pie Chart */}
        <div className="statistics-card min-w-0 overflow-hidden">
          <div className="statistics-card-title">{t("statistics.libraryMastery")}</div>
          {libraryMasteryData.length > 0 ? (
            <div className="flex flex-col gap-4">
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie
                    data={libraryMasteryData.map((p) => ({ ...p, name: t(`statistics.statusPie.${p.name}`) }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={72}
                    paddingAngle={3}
                    dataKey="value"
                    label={false}
                    stroke="none"
                    isAnimationActive
                    animationDuration={500}
                  >
                    {libraryMasteryData.map((_, i) => (
                      <Cell key={i} fill={chartColors[i % chartColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    wrapperStyle={{ outline: "none" }}
                    content={(props) => (
                      <ChartTooltipContent
                        {...props}
                        contentStyle={tooltipStyle}
                        formatter={(v: number, n: string) => [String(v), n]}
                      />
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Clean legend below — no overlapping text */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 4px" }}>
                {libraryMasteryData.map((p, i) => (
                  <div
                    key={p.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      padding: "8px 10px",
                      borderRadius: 10,
                      background: "hsl(var(--muted) / 0.25)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: chartColors[i % chartColors.length],
                          flexShrink: 0,
                        }}
                        aria-hidden
                      />
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "hsl(var(--foreground))",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t(`statistics.statusPie.${p.name}`)}
                      </span>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "hsl(var(--foreground))", flexShrink: 0 }}>
                      {p.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="statistics-empty" style={{ minHeight: 220 }}>
              <Activity size={36} className="statistics-empty-icon" />
              <span>{t("statistics.noSrsData")}</span>
            </div>
          )}
        </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 min-w-0 mt-5">
        {/* Left Column: Complex Words */}
        <div className="statistics-card min-w-0 overflow-hidden flex flex-col">
          <div className="statistics-card-title">{t("statistics.difficultWords")}</div>
          {difficultCards.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, overflowY: "auto" }}>
              {difficultCards.map((card) => (
                <div
                  key={card.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    background: "hsl(var(--bg3) / 0.6)",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid hsl(var(--border) / 0.3)",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "hsl(var(--foreground))", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {card.front}
                    </div>
                    <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
                      {statusLabel(card.status)} · EF: {card.easiness_factor.toFixed(1)}
                      {card.incorrect_count > 0 && ` · ${t("statistics.errorsCount", { count: card.incorrect_count })}`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      onRepeatCard
                        ? onRepeatCard(card.deck_id, card.id)
                        : onSelectDeck(card.deck_id)
                    }
                    className="px-3 py-2 text-xs font-semibold rounded-xl transition-colors hover:opacity-90 flex-shrink-0 border-none cursor-pointer font-inherit"
                    style={{
                      background: chartColors[0],
                      color: equippedTheme === "cyberpunkTheme" ? "#0d0221" : "white",
                    }}
                  >
                    {t("statistics.repeat")}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="statistics-empty flex-1" style={{ minHeight: 180 }}>
              <Activity size={36} className="statistics-empty-icon" />
              <span>{t("statistics.noDifficultWordsData")}</span>
            </div>
          )}
        </div>

        {/* Right Column: Weekly Activity + Time Analytics */}
        <div className="flex flex-col gap-6 min-w-0">
          {/* Weekly Activity Bar Chart */}
          <div className="statistics-card min-w-0 overflow-hidden">
            <div className="statistics-card-title">{t("statistics.weeklyActivity")}</div>
            {weeklyStudyBarData.some((d) => d.minutes > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={weeklyStudyBarData} margin={{ top: 12, right: 12, left: 8, bottom: 8 }}>
                <defs>
                  <linearGradient id="weeklyBarGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={1} />
                    <stop offset="100%" stopColor="hsl(var(--accent2))" stopOpacity={0.8} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 8" stroke="hsl(var(--border))" strokeOpacity={0.35} vertical={false} />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))", opacity: 0.9 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))", opacity: 0.9 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  wrapperStyle={{ outline: "none" }}
                  content={(props) => (
                    <ChartTooltipContent
                      {...props}
                      contentStyle={tooltipStyle}
                      formatter={(v: number) => [formatTime(v), t("statistics.learningTime")]}
                    />
                  )}
                />
                <Bar dataKey="minutes" fill="url(#weeklyBarGradient)" radius={[6, 6, 0, 0]} isAnimationActive animationDuration={400} />
              </BarChart>
            </ResponsiveContainer>
            ) : (
              <div className="statistics-empty" style={{ minHeight: 200 }}>
                <Activity size={36} className="statistics-empty-icon" />
                <span>{t("statistics.noData")}</span>
              </div>
            )}
          </div>

          {/* Time Analytics */}
          <div className="statistics-card min-w-0 overflow-hidden">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <span className="statistics-card-title" style={{ marginBottom: 0, fontSize: 16 }}>{t("statistics.timeAnalytics")}</span>
            </div>
            <div className="flex flex-col gap-6">
              <TimeActivityChart
                title={t("statistics.learningTime")}
                data={learningTimeChartData}
                strokeColor={chartColors[0]}
                formatValue={formatTime}
                tooltipContentStyle={tooltipStyle}
                tooltipLabelKey={t("statistics.learningTime")}
              />
              <TimeActivityChart
                title={t("statistics.totalAppTime")}
                data={siteTimeChartData}
                strokeColor={chartColors[1]}
                formatValue={formatTime}
                tooltipContentStyle={tooltipStyle}
                tooltipLabelKey={t("statistics.totalAppTime")}
              />
            </div>
          </div>
        </div>
        </div>

        {/* Time Distribution — per-mode (cards, learn, test, games). Donut + legend list, no overlapping labels. */}
        <div className="statistics-card min-w-0 overflow-hidden mt-5">
          <div className="statistics-card-title" style={{ fontSize: 16, marginBottom: "1.25rem" }}>{t("statistics.timeDistribution")}</div>
          {timeDistributionData.length > 0 ? (
            <div className="flex flex-col gap-6">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={timeDistributionData.map((p) => ({ ...p, name: t(p.name) }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={72}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                    isAnimationActive
                    animationDuration={500}
                  >
                    {timeDistributionData.map((p, i) => (
                      <Cell key={p.key} fill={p.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    wrapperStyle={{ outline: "none" }}
                    content={(props) => {
                      if (!props.active || !props.payload?.length) return null;
                      const p = props.payload[0]?.payload as { name: string; value: number; percent: number };
                      return (
                        <div style={tooltipStyle}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{p.name}</div>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                            <span style={{ opacity: 0.9 }}>{formatTime(p.value)}</span>
                            <span style={{ fontWeight: 600 }}>{p.percent}%</span>
                          </div>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 4px" }}>
                {timeDistributionData.map((p) => (
                  <div
                    key={p.key}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      padding: "10px 12px",
                      borderRadius: 12,
                      background: "hsl(var(--muted) / 0.2)",
                    }}
                  >
                    {/* Top row: marker + name | percent + time */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: p.color,
                            flexShrink: 0,
                          }}
                          aria-hidden
                        />
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: "hsl(var(--foreground))",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t(p.name)}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: "hsl(var(--muted-foreground))" }}>{p.percent}%</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>{formatTimeCompact(p.value)}</span>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div
                      style={{
                        height: 8,
                        borderRadius: 99,
                        background: "hsl(var(--bg3))",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        className="progress-fill"
                        style={{
                          height: "100%",
                          width: `${Math.max(p.percent, 3)}%`,
                          borderRadius: 99,
                          backgroundColor: p.color,
                          boxShadow: `0 0 8px color-mix(in srgb, ${p.color} 30%, transparent)`,
                          transition: "width 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="statistics-empty" style={{ minHeight: 200 }}>
              <Activity size={40} className="statistics-empty-icon" />
              <p style={{ margin: 0, maxWidth: 320 }}>{t("statistics.noTimeDistributionData")}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
